import { Checkbox, Stack, TextField, Typography } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import _ from 'lodash';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useState } from 'react';
import { DialogBox } from 'components/DialogBox';
import TextArea from 'components/FormCreatorComponent/inputs/TextArea';
import { fieldMode, fieldsGroupType, fieldType } from 'components/FormCreatorComponent/types';
import { useNavigate } from 'react-router-dom';
import Radio from 'components/FormCreatorComponent/inputs/Radio';
import { getCurrentData } from 'state/reducers/content.reducers';

type props = {
	openDialog: boolean;
	setOpenDialog: (open: boolean) => void;
	type: string;
	BoNotes: string | null;
	setBoNotes: (note: string) => void;
	handleApprove: (note: string) => void;
	template?: string;
	hasWcIndications?: boolean;
};

type resendEmailProps = {
	openDialog: boolean;
	setOpenDialog: (open: boolean) => void;
	handleApprove: (note: string) => void;
};
export const ApproveRequestDialog: React.FC<props> = (props: props) => {
	const { openDialog, setOpenDialog, type, BoNotes, setBoNotes, handleApprove, hasWcIndications } = props;
	const t = useAppSelector(selectTranslations);
	const [checked, setChecked] = useState(false);

	return (
		<DialogBox
			openDialog={openDialog}
			handleClose={() => {
				setChecked(false);
				setOpenDialog(false);
			}}
			approveDisabled={!checked}
			title={
				_.isEqual(type, pendingRequestsTypes.GENERAL_SUPPORT) ? t('label.closeRequest') : t('label.ifToApprove')
			}
			handleApprove={() => {
				setOpenDialog(false);
				handleApprove('approve');
			}}
			handleReject={() => {
				setChecked(false);
				setOpenDialog(false);
			}}
		>
			<Stack flexDirection={'row'} alignItems={'flex-start'}>
				<Checkbox checked={checked} onClick={() => setChecked(!checked)} sx={{ paddingTop: 0 }} />
				<Stack spacing={0.5}>
					<Typography>{t('label.approveTerm')}</Typography>
					{hasWcIndications && (
						<Typography variant="subtitleBold" >
							{t('label.wcIndicationsInDialog')}
						</Typography>
					)}
				</Stack>
			</Stack>
		</DialogBox>
	);
};

export const CancelRequestDialog: React.FC<props> = (props: props) => {
	const { openDialog, setOpenDialog, type, handleApprove } = props;
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();

	return (
		<DialogBox
			approveLabel={t('button.stay')}
			cancelLabel={t('button.leave')}
			openDialog={openDialog}
			handleClose={() => {
				setOpenDialog(false);
			}}
			title={t('label.cancelRequest')}
			handleApprove={() => {
				setOpenDialog(false);
			}}
			handleReject={() => {
				navigate(-1);
			}}
		>
			<Typography>{t('label.cancelWarning')}</Typography>
		</DialogBox>
	);
};

export const RejectRequestDialog: React.FC<props> = (props: props) => {
	const { openDialog, setOpenDialog, type, BoNotes, setBoNotes, handleApprove, template = 'regular' } = props;
	const t = useAppSelector(selectTranslations);
	const currentData = useAppSelector(getCurrentData) || [];
	const tpl = getRejectTemplate(template, t, setBoNotes, currentData, BoNotes);
	return (
		<DialogBox
			openDialog={openDialog}
			handleClose={() => setOpenDialog(false)}
			title={tpl.title}
			handleApprove={() => {
				if (_.isEmpty(BoNotes)) {
					setBoNotes('');
					return false;
				}
				setOpenDialog(false);
				handleApprove(tpl.action);
			}}
			handleReject={() => {
				setOpenDialog(false);
			}}
		>
			<Typography>{tpl.subtitle}</Typography>
			{tpl.body}
		</DialogBox>
	);
};

export const getRejectTemplate = (
	template: string,
	t: any,
	setBoNotes: any,
	translation: any,
	BoNotes?: string | null
) => {
	let title = t('label.rejectQuestionTitle');
	let subtitle = t('label.rejectQuestionSubtitle');
	let action = 'reject';
	let body = (
		<TextArea
			valid={_.isNull(BoNotes) || BoNotes !== ''}
			cField={{
				type: fieldType.text,
				mode: fieldMode.readWithEnableWrite,
				props: {
					name: 'bo_notes',
					label: '',
					required: true,
					value: BoNotes === null ? '' : BoNotes,
					maxLength: 200,
					minLength: 4,
				},
				validation: [{ requierd: true }],
			}}
			handleChange={(e: any) => {
				setBoNotes(e.target.value);
			}}
			initialValidation={[]}
		></TextArea>
	);

	switch (template) {
		case 'generalSupport':
			title = t('label.closeRequest');
			subtitle = t('label.closeRequestSummary');
			break;
		case 'beneficiaryRejectForFix':
			title = t('label.beneficiaryRejectForFixTitle');
			subtitle = t('label.beneficiaryRejectForFixSummary');
			action = 'DeclinedForRepair';
			break;
		case 'beneficiaryReject':
			title = t('label.beneficiaryRejectTitle');
			subtitle = t('label.beneficiaryRejectSummary');
			let options = _.filter(
				translation,
				(item) => item && _.startsWith(item?.key, 'page.beneficiary.bo.reject.reason.')
			);
			options = options.map((item) => {
				return {
					value: item.key,
					label: item.he,
				};
			});
			body = (
				<Radio
					valid={_.isNull(BoNotes) || BoNotes !== ''}
					cField={{
						type: fieldType.text,
						mode: fieldMode.readWithEnableWrite,
						props: {
							name: 'bo_notes',
							label: '',
							flex: fieldsGroupType.col,
							required: false,
							options,
							value: BoNotes === null ? '' : BoNotes,
						},
						validation: [{ requierd: false }],
					}}
					handleChange={(e: any) => {
						setBoNotes(e.target.value);
					}}
					initialValidation={[]}
				/>
			);
			break;
	}

	return {
		title,
		body,
		subtitle,
		action,
	};
};

export const ResendWelcomeEmailDialog: React.FC<resendEmailProps> = (props: resendEmailProps) => {
	const { openDialog, setOpenDialog, handleApprove } = props;
	const t = useAppSelector(selectTranslations);

	return (
		<DialogBox
			openDialog={openDialog}
			handleClose={() => {
				setOpenDialog(false);
			}}
			title={t('label.ifToApproveResendWelcomeEmail')}
			handleApprove={() => {
				setOpenDialog(false);
				handleApprove('approve');
			}}
			handleReject={() => {
				setOpenDialog(false);
			}}
		>
			<Stack flexDirection={'row'} alignItems={'center'}></Stack>
		</DialogBox>
	);
};
